import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusIcon, PencilSquareIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import apiService from '../../../services/ApiService';
import Button from '../../ui/Button';
import ConfirmDialog from '../../ui/ConfirmDialog';
import { useToast } from '../../../contexts/ToastContext';
import { VerificationChip } from '../PerformanceRecords';
import PerformanceRecordFormModal, { PerfRecordTypeMeta } from './PerformanceRecordFormModal';
import type { PerformanceRecord } from '../../../types/buildExpansion';

/**
 * Owner panel listing every performance record (public and private) with
 * add, edit, and archive actions. Dyno sessions are grouped separately from
 * timed/speed results. Verification status is read-only for owners.
 */

const formatDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const recordValue = (record: PerformanceRecord): string | null => {
  if (record.record_type === 'dyno') {
    const unit =
      record.unit_primary && record.unit_primary.toLowerCase() === 'kw'
        ? 'kw'
        : record.measurement_location === 'wheel'
          ? 'whp'
          : 'hp';
    if (record.display_value) {
      return `${record.display_value} ${unit}`;
    }
    if (record.value_primary == null) return null;
    return `${record.value_primary} ${unit}`;
  }
  if (record.display_value) {
    return !record.unit_primary || record.unit_primary === 's'
      ? record.display_value
      : `${record.display_value} ${record.unit_primary}`;
  }
  if (record.value_primary == null) return null;
  return `${record.value_primary}${record.unit_primary ? ` ${record.unit_primary}` : ''}`;
};

interface RecordRowProps {
  record: PerformanceRecord;
  onEdit: (record: PerformanceRecord) => void;
  onArchive: (record: PerformanceRecord) => void;
  busy: boolean;
  fullWidthActions?: boolean;
}

const RecordRow: React.FC<RecordRowProps> = ({
  record,
  onEdit,
  onArchive,
  busy,
  fullWidthActions = false,
}) => {
  const value = recordValue(record);
  const meta = [
    [record.venue_name, record.layout_name].filter(Boolean).join(' · '),
    record.record_type === 'dyno' && record.measurement_location
      ? record.measurement_location === 'wheel'
        ? 'Wheel'
        : 'Crank'
      : null,
    formatDate(record.date_recorded),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <li className="rounded-lg border border-white/[0.06] bg-[#0c0c0c]/50 px-3.5 py-3">
      <div
        className={`flex gap-x-4 gap-y-3 ${
          fullWidthActions ? 'flex-col' : 'flex-wrap items-start justify-between'
        }`}
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            {value && (
              <span className="text-lg font-semibold text-white tabular-nums">{value}</span>
            )}
            {record.title && <span className="text-sm text-gray-300">{record.title}</span>}
          </div>
          {meta && <p className="text-xs text-gray-500 mt-1">{meta}</p>}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {record.is_personal_best && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-orange-500/10 text-orange-300 border border-orange-500/25">
                Personal best
              </span>
            )}
            <VerificationChip status={record.verification_status} />
            {!record.is_public && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-[#111] text-gray-400 border border-white/[0.08]">
                Private
              </span>
            )}
          </div>
        </div>
        <div className={`flex gap-2 ${fullWidthActions ? 'flex-col w-full' : 'items-center shrink-0'}`}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => onEdit(record)}
            disabled={busy}
            fullWidth={fullWidthActions}
            className="min-h-[44px] justify-center"
            aria-label={`Edit ${record.record_type_label} record${record.title ? ` ${record.title}` : ''}`}
          >
            <PencilSquareIcon className="w-4 h-4" aria-hidden />
            Edit
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onArchive(record)}
            disabled={busy}
            fullWidth={fullWidthActions}
            className="min-h-[44px] justify-center"
            aria-label={`Archive ${record.record_type_label} record${record.title ? ` ${record.title}` : ''}`}
          >
            <ArchiveBoxIcon className="w-4 h-4" aria-hidden />
            Archive
          </Button>
        </div>
      </div>
    </li>
  );
};

export interface PerformanceRecordManagerProps {
  vehicleId: number;
  /** Called after any create, update, or archive so the parent can refresh. */
  onChanged?: () => void;
  /** Flatten chrome when nested inside Manage Build section cards. */
  embedded?: boolean;
  className?: string;
}

export const PerformanceRecordManager: React.FC<PerformanceRecordManagerProps> = ({
  vehicleId,
  onChanged,
  embedded = false,
  className = '',
}) => {
  const { showToast } = useToast();
  const [records, setRecords] = useState<PerformanceRecord[]>([]);
  const [recordTypes, setRecordTypes] = useState<PerfRecordTypeMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PerformanceRecord | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<PerformanceRecord | null>(null);
  const [archiving, setArchiving] = useState(false);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const { records: fetched, record_types } = await apiService.getPerformanceRecordList(
        vehicleId
      );
      setRecords(fetched);
      setRecordTypes(record_types);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load performance records.');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const dynoRecords = useMemo(
    () => records.filter((r) => r.record_type === 'dyno'),
    [records]
  );
  const otherGroups = useMemo(() => {
    const map = new Map<string, { label: string; items: PerformanceRecord[] }>();
    records
      .filter((r) => r.record_type !== 'dyno')
      .forEach((record) => {
        const existing = map.get(record.record_type);
        if (existing) {
          existing.items.push(record);
        } else {
          map.set(record.record_type, {
            label: record.record_type_label || record.record_type,
            items: [record],
          });
        }
      });
    return Array.from(map.values());
  }, [records]);

  const openCreate = () => {
    setEditingRecord(null);
    setFormOpen(true);
  };
  const openEdit = (record: PerformanceRecord) => {
    setEditingRecord(record);
    setFormOpen(true);
  };

  const handleSaved = () => {
    void load();
    onChanged?.();
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      await apiService.archivePerformanceRecord(vehicleId, archiveTarget.id);
      showToast('Record archived.', 'success');
      setArchiveTarget(null);
      void load();
      onChanged?.();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to archive the record.', 'error');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <section
      className={`${
        embedded ? 'space-y-5' : 'card p-5 sm:p-6 space-y-5'
      } ${className}`.trim()}
      aria-label="Manage performance records"
    >
      <div
        className={`flex gap-3 ${
          embedded ? 'flex-col' : 'flex-wrap items-center justify-between'
        }`}
      >
        <div>
          {!embedded ? (
            <h3 className="text-sm font-semibold text-white">Performance records</h3>
          ) : null}
          <p className={`text-xs text-gray-500 ${embedded ? '' : 'mt-0.5'}`}>
            Verification is handled by shops, events, or admins and cannot be set here.
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={openCreate}
          fullWidth={embedded}
          className="min-h-[44px] justify-center"
        >
          <PlusIcon className="w-4 h-4" aria-hidden />
          Add record
        </Button>
      </div>

      {loading && (
        <div className="flex items-center gap-3 text-sm text-gray-400" role="status">
          <span className="spinner-sm" aria-hidden />
          Loading records…
        </div>
      )}

      {!loading && loadError && (
        <div
          className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm"
          role="alert"
        >
          {loadError}{' '}
          <button
            type="button"
            onClick={() => {
              setLoading(true);
              void load();
            }}
            className="underline hover:text-white transition-colors cursor-pointer"
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !loadError && records.length === 0 && (
        <p className="text-sm text-gray-400">
          No performance records yet. Add lap times, drag results, or dyno sessions to build the
          performance history.
        </p>
      )}

      {!loading && !loadError && dynoRecords.length > 0 && (
        <div>
          <h4 className="text-meta font-medium">Dyno sessions</h4>
          <ul className="mt-2 space-y-2 list-none">
            {dynoRecords.map((record) => (
              <RecordRow
                key={record.id}
                record={record}
                onEdit={openEdit}
                onArchive={setArchiveTarget}
                busy={archiving}
                fullWidthActions={embedded}
              />
            ))}
          </ul>
        </div>
      )}

      {!loading &&
        !loadError &&
        otherGroups.map((group) => (
          <div key={group.label}>
            <h4 className="text-meta font-medium">{group.label}</h4>
            <ul className="mt-2 space-y-2 list-none">
              {group.items.map((record) => (
                <RecordRow
                  key={record.id}
                  record={record}
                  onEdit={openEdit}
                  onArchive={setArchiveTarget}
                  busy={archiving}
                  fullWidthActions={embedded}
                />
              ))}
            </ul>
          </div>
        ))}

      <PerformanceRecordFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        vehicleId={vehicleId}
        record={editingRecord}
        recordTypes={recordTypes.length > 0 ? recordTypes : undefined}
        onSaved={handleSaved}
      />

      <ConfirmDialog
        isOpen={archiveTarget !== null}
        title="Archive this record?"
        description="The record is removed from the build page and personal bests are recalculated. Archived records cannot be restored from here."
        confirmLabel="Archive record"
        destructive
        loading={archiving}
        loadingLabel="Archiving…"
        onConfirm={handleArchive}
        onCancel={() => {
          if (!archiving) setArchiveTarget(null);
        }}
        closeOnOverlayClick={!archiving}
      />
    </section>
  );
};

export default PerformanceRecordManager;
